import * as React from 'react';
import AppBar from '@mui/material/AppBar';
import CssBaseline from '@mui/material/CssBaseline';
import Divider from '@mui/material/Divider';
import Drawer from '@mui/material/Drawer';
import IconButton from '@mui/material/IconButton';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import ListSubheader from '@mui/material/ListSubheader';
import MenuIcon from '@mui/icons-material/Menu';
import Toolbar from '@mui/material/Toolbar';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { accesPageClub, estAdmin, estAdministrateurSeul, estCoach, useMoi } from './Auth/moi.ts';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Avatar from '@mui/material/Avatar';
import Tooltip from '@mui/material/Tooltip';
import ListItemIcon from '@mui/material/ListItemIcon';
import SettingsIcon from '@mui/icons-material/Settings';
import LogoutIcon from '@mui/icons-material/Logout';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import TimerIcon from '@mui/icons-material/Timer';
import WatchIcon from '@mui/icons-material/Watch';
import LeverIcon from '@mui/icons-material/AlarmOutlined';
import AccueilIcon from '@mui/icons-material/HomeOutlined';
import AlluresIcon from '@mui/icons-material/SpeedOutlined';
import SeancesIcon from '@mui/icons-material/FitnessCenterOutlined';
import CalendrierIcon from '@mui/icons-material/CalendarMonthOutlined';
import JournalIcon from '@mui/icons-material/MenuBookOutlined';
import AgendaIcon from '@mui/icons-material/EmojiEventsOutlined';
import UtilisateursIcon from '@mui/icons-material/GroupOutlined';
import ClubsAdminIcon from '@mui/icons-material/CorporateFareOutlined';
import AthletesIcon from '@mui/icons-material/DirectionsRunOutlined';
import ClubIcon from '@mui/icons-material/GroupsOutlined';
import ConfidentialiteIcon from '@mui/icons-material/PrivacyTipOutlined';
import GuideIcon from '@mui/icons-material/HelpOutline';
import { CHEMIN_GUIDE_NOTATION } from './Guide/GuideNotation.tsx';
import { CHEMIN_CONFIDENTIALITE } from './Confidentialite/Confidentialite.tsx';
import { CHEMIN_CLUBS_ADMIN } from './Administration/ClubsAdmin.tsx';

type Entree = { chemin: string; libelle: string; icone: React.ReactNode };

// Entrees principales, dans l'ordre de la barre (coach : reservee au coach ;
// club : aux coachs et administrateurs d'un club).
const ENTREES: (Entree & { coach?: boolean; club?: boolean })[] = [
  { chemin: '/', libelle: 'Accueil', icone: <AccueilIcon /> },
  { chemin: '/club', libelle: 'Club', icone: <ClubIcon />, club: true },
  { chemin: '/athletes', libelle: 'Mes athlètes', icone: <AthletesIcon />, coach: true },
  { chemin: '/planning', libelle: 'Calendrier', icone: <CalendrierIcon /> },
  { chemin: '/journal', libelle: 'Journal', icone: <JournalIcon /> },
  { chemin: '/seances', libelle: 'Séances', icone: <SeancesIcon />, coach: true },
];

// Sous-menu "Outils" (coach : reservee au coach).
const OUTILS: (Entree & { coach?: boolean })[] = [
  { chemin: '/allures', libelle: 'Mes allures', icone: <AlluresIcon /> },
  { chemin: '/lever', libelle: 'Heure de lever', icone: <LeverIcon /> },
  { chemin: '/passages', libelle: 'Temps de passage', icone: <TimerIcon /> },
  { chemin: '/bracelet', libelle: 'Bracelet', icone: <WatchIcon /> },
  { chemin: CHEMIN_GUIDE_NOTATION, libelle: 'Guide de notation', icone: <GuideIcon />, coach: true },
];

// Menu "Administration", affiche aux seuls administrateurs (cf runplan.admin.emails).
const ADMINISTRATION: Entree[] = [
  { chemin: '/utilisateurs', libelle: 'Utilisateurs', icone: <UtilisateursIcon /> },
  { chemin: CHEMIN_CLUBS_ADMIN, libelle: 'Clubs', icone: <ClubsAdminIcon /> },
];

// Pages de l'entrainement personnel, sans objet pour un administrateur de club qui n'entraine pas.
const PAGES_ENTRAINEMENT = ['/planning', '/journal'];

const AGENDA: Entree = { chemin: '/calendrier', libelle: 'Agenda', icone: <AgendaIcon /> };

// Page courante : correspondance exacte pour l'accueil, par prefixe sinon
// (ex : /seances/123 -> Seances).
function estActive(chemin: string, courant: string): boolean {
  return chemin === '/' ? courant === '/' : courant === chemin || courant.startsWith(chemin + '/');
}

// Nom de la page courante, affiche dans la barre a cote de "Running Plan".
function titrePage(courant: string): string | null {
  if (courant === '/seances/nouvelle') return 'Nouvelle séance';
  if (courant.startsWith('/seances/')) return 'Modifier une séance';
  const autres: Entree[] = [
    { chemin: '/modeles', libelle: 'Séances modèles', icone: null },
    { chemin: '/preferences', libelle: 'Préférences', icone: null },
    { chemin: CHEMIN_CONFIDENTIALITE, libelle: 'Confidentialité', icone: null },
  ];
  return [...ENTREES, AGENDA, ...OUTILS, ...ADMINISTRATION, ...autres].find((e) => estActive(e.chemin, courant))?.libelle ?? null;
}

const LARGEUR_TIROIR = 260;

/**
 * Barre de navigation : boutons sur ecran large ; sur mobile / tablette,
 * bouton "hamburger" qui ouvre un tiroir lateral avec toutes les entrees.
 */
const DrawerAppBar = ({ user, handleLogout, refEmplacement }: {
  props?: any;
  user: any;
  handleLogout: () => void;
  // Emplacement des controles propres a la page (cf BarrePage.tsx).
  refEmplacement?: (element: HTMLElement | null) => void;
}) => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const titre = titrePage(pathname);
  // L'athlete n'a pas acces aux seances (definies par son coach).
  const moi = useMoi();
  // Administrateur de club qui n'entraine pas : ni accueil (seances du
  // jour), ni calendrier, ni journal.
  const sansEntrainement = estAdministrateurSeul(moi);
  // Role pas encore connu : accueil seulement, plutot qu'un menu d'athlete
  // remplace ensuite par celui du role.
  const entrees = !moi ? ENTREES.filter((e) => e.chemin === '/') : ENTREES.filter((e) => (!e.coach || estCoach(moi)) && (!e.club || accesPageClub(moi))
    && !(sansEntrainement && PAGES_ENTRAINEMENT.includes(e.chemin)));
  // Outils de l'entrainement (allures, temps de passage...) : sans objet sans entrainement.
  const outils = !moi || sansEntrainement ? [] : OUTILS.filter((e) => !e.coach || estCoach(moi));
  const administration = estAdmin(moi) ? ADMINISTRATION : [];

  // Menu utilisateur : photo du profil Google (claim "picture" du jeton
  // SSO, cf Login.tsx), qui ouvre Préférences / Déconnexion.
  const [ancreMenuUtilisateur, setAncreMenuUtilisateur] = React.useState<null | HTMLElement>(null);
  const menuUtilisateurOuvert = Boolean(ancreMenuUtilisateur);
  const fermerMenuUtilisateur = () => setAncreMenuUtilisateur(null);

  const ouvrirPreferences = () => {
    fermerMenuUtilisateur();
    navigate('/preferences');
  };

  const ouvrirConfidentialite = () => {
    fermerMenuUtilisateur();
    navigate(CHEMIN_CONFIDENTIALITE);
  };

  const deconnecter = () => {
    fermerMenuUtilisateur();
    handleLogout();
    navigate('/');
  };

  // Menus "Outils" et "Administration" (ecran large).
  const [ancreMenuOutils, setAncreMenuOutils] = React.useState<null | HTMLElement>(null);
  const menuOutilsOuvert = Boolean(ancreMenuOutils);
  const fermerMenuOutils = () => setAncreMenuOutils(null);
  const [ancreMenuAdmin, setAncreMenuAdmin] = React.useState<null | HTMLElement>(null);
  const menuAdminOuvert = Boolean(ancreMenuAdmin);
  const fermerMenuAdmin = () => setAncreMenuAdmin(null);
  const allerA = (chemin: string) => {
    fermerMenuOutils();
    fermerMenuAdmin();
    navigate(chemin);
  };

  // Tiroir (mobile / tablette).
  const [tiroirOuvert, setTiroirOuvert] = React.useState(false);

  const nomUtilisateur: string = user?.name || user?.email || 'Utilisateur';

  const boutonBarre = (e: Entree) => (
    <Link key={e.chemin} to={e.chemin} reloadDocument>
      <Button
        sx={{
          color: '#fff',
          borderBottom: 2,
          borderColor: estActive(e.chemin, pathname) ? '#fff' : 'transparent',
          borderRadius: 0,
        }}
      >
        {e.libelle}
      </Button>
    </Link>
  );

  const entreeTiroir = (e: Entree) => (
    <ListItemButton
      key={e.chemin}
      component={Link}
      to={e.chemin}
      reloadDocument
      selected={estActive(e.chemin, pathname)}
      onClick={() => setTiroirOuvert(false)}
    >
      <ListItemIcon>{e.icone}</ListItemIcon>
      <ListItemText primary={e.libelle} />
    </ListItemButton>
  );

  const tiroir = (
    <Box sx={{ width: LARGEUR_TIROIR }} role="navigation" aria-label="Menu principal">
      <Typography variant="h6" sx={{ px: 2, py: 1.5 }}>
        Running Plan
      </Typography>
      <Divider />
      <List>
        {entrees.map(entreeTiroir)}
      </List>
      {outils.length > 0 && (
        <>
          <Divider />
          <List subheader={<ListSubheader component="div">Outils</ListSubheader>}>
            {outils.map(entreeTiroir)}
          </List>
        </>
      )}
      <Divider />
      <List>
        {entreeTiroir(AGENDA)}
      </List>
      <Divider />
      {administration.length > 0 && (
        <>
          <List subheader={<ListSubheader component="div">Administration</ListSubheader>}>
            {administration.map(entreeTiroir)}
          </List>
          <Divider />
        </>
      )}
      <List>
        <ListItemButton
          selected={estActive('/preferences', pathname)}
          onClick={() => {
            setTiroirOuvert(false);
            navigate('/preferences');
          }}
        >
          <ListItemIcon><SettingsIcon /></ListItemIcon>
          <ListItemText primary="Préférences" />
        </ListItemButton>
        <ListItemButton
          selected={estActive(CHEMIN_CONFIDENTIALITE, pathname)}
          onClick={() => {
            setTiroirOuvert(false);
            navigate(CHEMIN_CONFIDENTIALITE);
          }}
        >
          <ListItemIcon><ConfidentialiteIcon /></ListItemIcon>
          <ListItemText primary="Confidentialité et cookies" />
        </ListItemButton>
        <ListItemButton
          onClick={() => {
            setTiroirOuvert(false);
            handleLogout();
            navigate('/');
          }}
        >
          <ListItemIcon><LogoutIcon /></ListItemIcon>
          <ListItemText primary="Déconnexion" />
        </ListItemButton>
      </List>
    </Box>
  );

  return (
    <div>
      <CssBaseline />
      <AppBar component="nav">
        <Toolbar>
          {/* Mobile / tablette : bouton du tiroir */}
          <IconButton
            color="inherit"
            edge="start"
            aria-label="Ouvrir le menu"
            onClick={() => setTiroirOuvert(true)}
            sx={{ mr: 1, display: { md: 'none' } }}
          >
            <MenuIcon />
          </IconButton>
          {/* "Running Plan - <page>" (sur mobile, le nom de la page seul),
              suivi des controles de la page */}
          <Box sx={{ flexGrow: 1, minWidth: 0, display: 'flex', alignItems: 'center', gap: 2 }}>
            <Typography variant="h6" component="div" noWrap sx={{ minWidth: 0 }}>
              <Box component="span" sx={{ display: { xs: titre ? 'none' : 'inline', sm: 'inline' } }}>
                Running Plan
              </Box>
              {titre && (
                <>
                  <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' }, mx: 1, opacity: 0.7 }}>-</Box>
                  {titre}
                </>
              )}
            </Typography>
            <Box ref={refEmplacement} sx={{ display: 'flex', alignItems: 'center', flexShrink: 0 }} />
          </Box>

          {/* Ecran large : boutons */}
          <Box sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'center' }}>
            {entrees.map(boutonBarre)}
            {outils.length > 0 && (
              <>
                <Button
                  sx={{
                    color: '#fff',
                    borderBottom: 2,
                    borderColor: OUTILS.some((o) => estActive(o.chemin, pathname)) ? '#fff' : 'transparent',
                    borderRadius: 0,
                  }}
                  endIcon={<KeyboardArrowDownIcon />}
                  onClick={(e) => setAncreMenuOutils(e.currentTarget)}
                  aria-controls={menuOutilsOuvert ? 'menu-outils' : undefined}
                  aria-haspopup="true"
                  aria-expanded={menuOutilsOuvert ? 'true' : undefined}
                >
                  Outils
                </Button>
                <Menu
                  id="menu-outils"
                  anchorEl={ancreMenuOutils}
                  open={menuOutilsOuvert}
                  onClose={fermerMenuOutils}
                  anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                  transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                >
                  {outils.map((o) => (
                    <MenuItem key={o.chemin} onClick={() => allerA(o.chemin)}>
                      <ListItemIcon>{o.icone}</ListItemIcon>
                      {o.libelle}
                    </MenuItem>
                  ))}
                </Menu>
              </>
            )}
            {boutonBarre(AGENDA)}
            {administration.length > 0 && (
              <>
                <Button
                  sx={{
                    color: '#fff',
                    borderBottom: 2,
                    borderColor: administration.some((o) => estActive(o.chemin, pathname)) ? '#fff' : 'transparent',
                    borderRadius: 0,
                  }}
                  endIcon={<KeyboardArrowDownIcon />}
                  onClick={(e) => setAncreMenuAdmin(e.currentTarget)}
                  aria-controls={menuAdminOuvert ? 'menu-administration' : undefined}
                  aria-haspopup="true"
                  aria-expanded={menuAdminOuvert ? 'true' : undefined}
                >
                  Administration
                </Button>
                <Menu
                  id="menu-administration"
                  anchorEl={ancreMenuAdmin}
                  open={menuAdminOuvert}
                  onClose={fermerMenuAdmin}
                  anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                  transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                >
                  {administration.map((o) => (
                    <MenuItem key={o.chemin} onClick={() => allerA(o.chemin)}>
                      <ListItemIcon>{o.icone}</ListItemIcon>
                      {o.libelle}
                    </MenuItem>
                  ))}
                </Menu>
              </>
            )}
          </Box>

          <Tooltip title={nomUtilisateur}>
            <IconButton
              onClick={(e) => setAncreMenuUtilisateur(e.currentTarget)}
              sx={{ ml: 2, p: 0 }}
              aria-label="Menu utilisateur"
              aria-controls={menuUtilisateurOuvert ? 'menu-utilisateur' : undefined}
              aria-haspopup="true"
              aria-expanded={menuUtilisateurOuvert ? 'true' : undefined}
            >
              <Avatar
                alt={nomUtilisateur}
                src={user?.picture}
                // Les photos Google (lh3.googleusercontent.com) sont parfois
                // refusees quand un referer est envoye.
                imgProps={{ referrerPolicy: 'no-referrer' }}
                sx={{ width: 36, height: 36 }}
              >
                {nomUtilisateur.charAt(0).toUpperCase()}
              </Avatar>
            </IconButton>
          </Tooltip>
          <Menu
            id="menu-utilisateur"
            anchorEl={ancreMenuUtilisateur}
            open={menuUtilisateurOuvert}
            onClose={fermerMenuUtilisateur}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          >
            <MenuItem onClick={ouvrirPreferences}>
              <ListItemIcon><SettingsIcon fontSize="small" /></ListItemIcon>
              Préférences
            </MenuItem>
            <MenuItem onClick={ouvrirConfidentialite}>
              <ListItemIcon><ConfidentialiteIcon fontSize="small" /></ListItemIcon>
              Confidentialité et cookies
            </MenuItem>
            <MenuItem onClick={deconnecter}>
              <ListItemIcon><LogoutIcon fontSize="small" /></ListItemIcon>
              Déconnexion
            </MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>

      <Drawer
        variant="temporary"
        open={tiroirOuvert}
        onClose={() => setTiroirOuvert(false)}
        ModalProps={{ keepMounted: true }}
        sx={{ display: { xs: 'block', md: 'none' } }}
      >
        {tiroir}
      </Drawer>
    </div>
  );
};

export default DrawerAppBar;
